import { supabase, isSupabaseConfigured } from './supabaseClient';

export interface VendorClickEvent {
  vendor_id: string;
  offer_id: number | null;
  affiliate_url: string;
  source: string;
  device_platform: 'ios' | 'android' | 'web';
  peptide_name?: string | null;
  app_version?: string;
  event_type?: string;
}

/**
 * Get app version for web (from package.json or default)
 */
function getAppVersion(): string {
  // For web, we can use a version from env or default
  return import.meta.env.VITE_APP_VERSION || '1.0.0';
}

/**
 * Tracks a vendor/affiliate link click by inserting a record into Supabase.
 * This function is non-blocking and will not throw errors to avoid disrupting
 * the user experience if tracking fails.
 * 
 * @param event - The click event data to track
 */
export async function trackVendorClick(event: VendorClickEvent): Promise<void> {
  try {
    // Don't track if Supabase is not configured
    if (!isSupabaseConfigured()) {
      if (import.meta.env.DEV) {
        console.warn('Supabase not configured, skipping click tracking');
      }
      return;
    }

    const appVersion = event.app_version || getAppVersion();
    const eventType = event.event_type || 'vendor_click';

    const { error } = await supabase
      .from('peptide_finder_app_events')
      .insert({
        vendor_id: event.vendor_id,
        offer_id: event.offer_id,
        affiliate_url: event.affiliate_url,
        source: event.source,
        device_platform: event.device_platform,
        peptide_name: event.peptide_name ?? null,
        app_version: appVersion,
        event_type: eventType,
      });

    if (error) {
      if (import.meta.env.DEV) {
        console.error('Error tracking vendor click:', error);
      }
    }
  } catch (err) {
    // Silently fail - tracking should not block user experience
    if (import.meta.env.DEV) {
      console.error('Failed to track vendor click:', err);
    }
  }
}

